import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceDot, ResponsiveContainer,
} from "recharts";
import { ArrowRight } from "lucide-react";
import { EASE, Counter } from "@/components/motion";

/* ---------- PROJECT 01: FIVE-STAGE FEASIBILITY EXPLORER ---------- */

const MarketMap = () => {
  const nodes = [
    { x: 200, y: 40, label: "City offices" },
    { x: 340, y: 120, label: "Leisure — Tower Bridge" },
    { x: 200, y: 205, label: "London Bridge station" },
    { x: 60, y: 120, label: "Events & culture" },
  ];
  return (
    <div className="mt-8 border border-hairline bg-stone2/60 p-6" data-testid="market-map">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-fog">Demand generators · London Bridge corridor</p>
      <svg viewBox="0 0 400 250" className="w-full" role="img" aria-label="Simplified diagram of demand generators around the Tower Bridge site">
        {nodes.map((n, i) => (
          <motion.line
            key={n.label}
            x1="200" y1="125" x2={n.x} y2={n.y}
            stroke="#D4C5B0" strokeWidth="1" strokeDasharray="3 4"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.15, duration: 0.7, ease: EASE }}
          />
        ))}
        {nodes.map((n, i) => (
          <motion.g
            key={n.label}
            initial={{ opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 + i * 0.15, duration: 0.5, ease: EASE }}
          >
            <circle cx={n.x} cy={n.y} r="6" fill="#D4C5B0" />
            <text x={n.x} y={n.y - 14} textAnchor="middle" fontSize="10.5" fill="#4A4A4A" fontWeight="600" letterSpacing="1">{n.label}</text>
          </motion.g>
        ))}
        <motion.g
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <rect x="188" y="113" width="24" height="24" fill="#2A362D" transform="rotate(45 200 125)" />
          <text x="200" y="152" textAnchor="middle" fontSize="10" fontWeight="700" fill="#2A362D" letterSpacing="1.5">SITE</text>
        </motion.g>
      </svg>
      <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-fog">Simplified diagram — illustrative, not to scale</p>
    </div>
  );
};

const PositioningMatrix = () => {
  const points = [
    { x: 18, y: 76, label: "Budget branded" },
    { x: 34, y: 58, label: "Midscale independents" },
    { x: 58, y: 38, label: "Upscale full-service" },
    { x: 84, y: 16, label: "Luxury" },
  ];
  return (
    <div className="mt-8 border border-hairline bg-paper p-6" data-testid="positioning-matrix">
      <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-fog">Price–value positioning · London Bridge comp set</p>
      <svg viewBox="0 0 400 260" className="w-full" role="img" aria-label="Positioning matrix: price against service level, showing the proposed concept in the upper-midscale gap">
        <line x1="40" y1="220" x2="380" y2="220" stroke="#E5E1D8" strokeWidth="1" />
        <line x1="40" y1="220" x2="40" y2="20" stroke="#E5E1D8" strokeWidth="1" />
        <text x="210" y="246" textAnchor="middle" fontSize="10" fill="#8A8A8A" letterSpacing="2">PRICE →</text>
        <text x="16" y="130" fontSize="10" fill="#8A8A8A" letterSpacing="2" transform="rotate(-90 16 130)">SERVICE LEVEL →</text>
        {points.map((p, i) => (
          <g key={p.label}>
            <motion.circle
              cx={40 + p.x * 3.4}
              cy={20 + p.y * 2}
              r="5"
              fill="#D4C5B0"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + i * 0.12, duration: 0.5, ease: EASE }}
            />
            <text x={40 + p.x * 3.4} y={20 + p.y * 2 - 12} textAnchor="middle" fontSize="9.5" fill="#4A4A4A">{p.label}</text>
          </g>
        ))}
        <motion.g
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.9, duration: 0.6, ease: EASE }}
        >
          <rect x={40 + 38 * 3.4 - 9} y={20 + 46 * 2 - 9} width="18" height="18" fill="#2A362D" transform={`rotate(45 ${40 + 38 * 3.4} ${20 + 46 * 2})`} />
          <text x={40 + 38 * 3.4} y={20 + 46 * 2 - 16} textAnchor="middle" fontSize="10" fontWeight="700" fill="#2A362D">PROPOSED CONCEPT</text>
        </motion.g>
      </svg>
    </div>
  );
};

export const StagesExplorer = ({ stages }) => {
  const [active, setActive] = useState(0);
  const stage = stages[active];

  return (
    <div data-testid="stages-explorer">
      <div className="flex flex-nowrap gap-px overflow-x-auto border border-hairline bg-hairline" role="tablist" aria-label="Feasibility stages">
        {stages.map((s, i) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={active === i}
            data-testid={`stage-tab-${s.id}`}
            onClick={() => setActive(i)}
            className={`min-w-[110px] flex-1 whitespace-nowrap px-4 py-4 text-center transition-colors duration-300 ${
              active === i ? "bg-moss text-paper" : "bg-paper text-smoke hover:bg-stone2"
            }`}
          >
            <span className={`block text-[10px] font-semibold tracking-[0.22em] ${active === i ? "text-champagne" : "text-fog"}`}>{s.step}</span>
            <span className="block text-[12px] font-semibold uppercase tracking-[0.16em]">{s.name}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={stage.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="border border-t-0 border-hairline bg-paper p-8 sm:p-12"
          data-testid={`stage-panel-${stage.id}`}
        >
          <p className="font-serif text-3xl sm:text-4xl font-light italic text-ink">{stage.question}</p>
          {stage.body && <p className="mt-6 max-w-3xl text-base leading-relaxed text-smoke">{stage.body}</p>}

          {stage.chips.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {stage.chips.map((c) => (
                <span key={c} className="border border-hairline px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-fog">{c}</span>
              ))}
            </div>
          )}

          {stage.siteFacts && (
            <div className="mt-8 grid grid-cols-1 gap-px border border-hairline bg-hairline sm:grid-cols-3">
              {stage.siteFacts.map((f) => (
                <div key={f.k} className="bg-stone2 p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-fog">{f.k}</p>
                  <p className="mt-2 font-serif text-2xl font-light text-ink">{f.v}</p>
                </div>
              ))}
            </div>
          )}

          {stage.marketMap && <MarketMap />}
          {stage.matrix && <PositioningMatrix />}

          {stage.scenarios && (
            <div className="mt-8" data-testid="scenario-cards">
              <div className="grid grid-cols-1 gap-px border border-hairline bg-hairline md:grid-cols-2">
                {stage.scenarios.map((s) => (
                  <div key={s.name} className="bg-stone2 p-7">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-moss">{s.name}</p>
                    <ul className="mt-4 flex flex-col gap-2.5">
                      {s.lines.map((l) => (
                        <li key={l} className="border-t border-hairline pt-2.5 text-sm leading-relaxed text-smoke">{l}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] uppercase tracking-[0.16em] text-fog">{stage.scenarioNote}</p>
            </div>
          )}

          {stage.decisionGrid && (
            <div data-testid="decision-grid">
              <div className="grid grid-cols-1 gap-px border border-hairline bg-hairline sm:grid-cols-2">
                {stage.decisionGrid.map((d) => (
                  <div key={d.k} className="bg-paper p-6">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-moss">{d.k}</p>
                    <p className="mt-2 text-sm leading-relaxed text-smoke">{d.v}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 border-l-2 border-champagne pl-5 font-serif text-xl sm:text-2xl font-light italic leading-snug text-ink">
                {stage.finalLine}
              </p>
            </div>
          )}

          <div className="mt-10 flex items-center justify-between border-t border-hairline pt-6">
            <button
              onClick={() => setActive(Math.max(0, active - 1))}
              disabled={active === 0}
              data-testid="stage-prev"
              className="text-[12px] font-semibold uppercase tracking-[0.16em] text-fog transition-colors enabled:hover:text-ink disabled:opacity-30"
            >
              ← Previous
            </button>
            <span className="text-[11px] font-semibold tracking-[0.2em] text-fog">{stage.step} / 05</span>
            <button
              onClick={() => setActive(Math.min(stages.length - 1, active + 1))}
              disabled={active === stages.length - 1}
              data-testid="stage-next"
              className="text-[12px] font-semibold uppercase tracking-[0.16em] text-fog transition-colors enabled:hover:text-ink disabled:opacity-30"
            >
              Next →
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/* ---------- PROJECT 02: ROOMS ONLY → TOTAL HOTEL ---------- */

const DEPARTMENTS = [
  { key: "rooms", label: "Rooms", perRoom: 102.24 },
  { key: "fnb", label: "Food & Beverage", perRoom: 38.2 },
  { key: "events", label: "Events", perRoom: 14.1 },
  { key: "spa", label: "Spa", perRoom: 7.34 },
];

export const RevParToggle = () => {
  const [total, setTotal] = useState(false);
  const max = 161.88;

  return (
    <div className="border border-hairline bg-paper" data-testid="revpar-widget">
      <div className="flex flex-col gap-6 border-b border-hairline p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
        <div>
          <p className="overline-label">Baseline indicators · 250-room four-star, Guildford</p>
          <p className="mt-2 font-serif text-xl italic text-fog">RevPAR → total revenue → profitability.</p>
        </div>
        <div className="inline-flex border border-ink" role="group" aria-label="Revenue lens toggle">
          <button
            onClick={() => setTotal(false)}
            data-testid="toggle-rooms-only"
            aria-pressed={!total}
            className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${!total ? "bg-ink text-paper" : "text-ink hover:bg-stone2"}`}
          >
            Rooms only
          </button>
          <button
            onClick={() => setTotal(true)}
            data-testid="toggle-total-hotel"
            aria-pressed={total}
            className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${total ? "bg-moss text-paper" : "text-ink hover:bg-stone2"}`}
          >
            Total hotel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="border-b border-hairline p-8 sm:p-10 lg:col-span-5 lg:border-b-0 lg:border-r">
          <AnimatePresence mode="wait">
            <motion.div
              key={total ? "total" : "rooms"}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.45, ease: EASE }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-fog" data-testid="revpar-kpi-label">
                {total ? "TRevPAR — total revenue per available room" : "RevPAR — rooms revenue per available room"}
              </p>
              <p className="mt-3 font-serif text-7xl font-light text-ink sm:text-8xl" data-testid="revpar-kpi-value">
                <Counter value={total ? 161.88 : 102.24} decimals={2} prefix="£" duration={1.1} />
              </p>
              <div className="mt-6 flex gap-8">
                <div>
                  <p className="font-serif text-2xl font-light text-moss"><Counter value={85} suffix="%" duration={1} /></p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-fog">Occupancy</p>
                </div>
                <div>
                  <p className="font-serif text-2xl font-light text-moss"><Counter value={120.28} decimals={2} prefix="£" duration={1} /></p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-fog">ADR</p>
                </div>
                {total && (
                  <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
                    <p className="font-serif text-2xl font-light text-champagne"><Counter value={103.73} decimals={2} prefix="£" duration={1.2} /></p>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-fog">GOPPAR</p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="p-8 sm:p-10 lg:col-span-7">
          <p className="mb-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-fog">Revenue per available room, by department</p>
          <div className="flex flex-col gap-5">
            {DEPARTMENTS.map((d, i) => {
              const visible = d.key === "rooms" || total;
              return (
                <div key={d.key} data-testid={`dept-bar-${d.key}`}>
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <span className={`text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors duration-500 ${visible ? "text-ink" : "text-fog/40"}`}>
                      {d.label}
                    </span>
                    <span className={`font-serif text-lg transition-opacity duration-500 ${visible ? "text-moss opacity-100" : "opacity-0"}`}>
                      £{d.perRoom.toFixed(2)}
                    </span>
                  </div>
                  <div className="h-2 w-full bg-stone2">
                    <motion.div
                      className={d.key === "rooms" ? "h-full bg-ink" : "h-full bg-champagne"}
                      initial={false}
                      animate={{ width: visible ? `${(d.perRoom / max) * 100}%` : "0%" }}
                      transition={{ duration: 0.8, delay: visible ? i * 0.1 : 0, ease: EASE }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <AnimatePresence>
            {total && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mt-8 border-l-2 border-champagne pl-4 font-serif text-lg italic leading-snug text-smoke"
                data-testid="revpar-insight"
              >
                Nearly 60% more revenue per available room sat outside the rooms-only metric.
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

/* ---------- PROJECT 03: QUARTERLY F&B TIMELINE ---------- */

const FB_DATA = {
  ease: {
    name: "EASE",
    desc: "Limited-service concept",
    quarters: [
      { q: "Q1", revenue: 412, margin: 14.2, labour: 38, covers: 28.4 },
      { q: "Q2", revenue: 468, margin: 11.8, labour: 41, covers: 31.2 },
      { q: "Q3", revenue: 512, margin: -3.6, labour: 52, covers: 29.8 },
      { q: "Q4", revenue: 545, margin: 6.1, labour: 44, covers: 33.5 },
    ],
  },
  lum: {
    name: "LUM",
    desc: "Luxury full-service concept",
    quarters: [
      { q: "Q1", revenue: 890, margin: 9.5, labour: 46, covers: 19.6 },
      { q: "Q2", revenue: 945, margin: 7.2, labour: 48, covers: 21.1 },
      { q: "Q3", revenue: 1012, margin: -5.8, labour: 58, covers: 20.2 },
      { q: "Q4", revenue: 1088, margin: 3.4, labour: 50, covers: 23.0 },
    ],
  },
};

const METRICS = [
  { key: "revenue", label: "Revenue", unit: "£k", color: "#1A1A1A" },
  { key: "margin", label: "Margin", unit: "%", color: "#2A362D" },
  { key: "labour", label: "Labour", unit: "% of revenue", color: "#B49B78" },
  { key: "covers", label: "Covers", unit: "k", color: "#4A4A4A" },
];

const ChartTooltip = ({ active, payload, label, metric }) => {
  if (!active || !payload?.length) return null;
  const m = METRICS.find((x) => x.key === metric);
  return (
    <div className="border border-hairline bg-paper px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-fog">{label}</p>
      <p className="font-serif text-xl text-ink">
        {payload[0].value} <span className="text-sm text-fog">{m.unit}</span>
      </p>
    </div>
  );
};

export const FBTimeline = () => {
  const [hotel, setHotel] = useState("ease");
  const [metric, setMetric] = useState("revenue");
  const data = FB_DATA[hotel];
  const m = METRICS.find((x) => x.key === metric);
  const q3 = data.quarters[2];

  return (
    <div className="border border-hairline bg-paper" data-testid="fnb-widget">
      <div className="flex flex-col gap-6 border-b border-hairline p-8 sm:p-10 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="overline-label">Quarterly performance · two concepts</p>
          <p className="mt-2 font-serif text-xl italic text-fog">Demand → labour → margin. Follow the chain.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="inline-flex border border-ink" role="group" aria-label="Hotel concept">
            {Object.entries(FB_DATA).map(([key, h]) => (
              <button
                key={key}
                onClick={() => setHotel(key)}
                data-testid={`hotel-${key}`}
                aria-pressed={hotel === key}
                className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${hotel === key ? "bg-ink text-paper" : "text-ink hover:bg-stone2"}`}
              >
                {h.name}
              </button>
            ))}
          </div>
          <div className="inline-flex flex-wrap border border-hairline" role="group" aria-label="Metric">
            {METRICS.map((mm) => (
              <button
                key={mm.key}
                onClick={() => setMetric(mm.key)}
                data-testid={`metric-${mm.key}`}
                aria-pressed={metric === mm.key}
                className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${metric === mm.key ? "bg-moss text-paper" : "text-smoke hover:bg-stone2"}`}
              >
                {mm.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="p-6 sm:p-10 lg:col-span-8">
          <p className="mb-2 text-sm text-fog">
            <span className="font-semibold text-ink">{data.name}</span> — {data.desc} · {m.label} ({m.unit})
          </p>
          <div className="h-[320px] w-full" data-testid="fnb-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart key={`${hotel}-${metric}`} data={data.quarters} margin={{ top: 30, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid stroke="#E5E1D8" vertical={false} />
                <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#8A8A8A", letterSpacing: 2 }} axisLine={{ stroke: "#E5E1D8" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8A8A8A" }} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<ChartTooltip metric={metric} />} cursor={{ stroke: "#D4C5B0" }} />
                {metric === "margin" && <ReferenceLine y={0} stroke="#B49B78" strokeDasharray="4 4" />}
                {metric === "margin" && (
                  <ReferenceDot
                    x="Q3"
                    y={q3.margin}
                    r={7}
                    fill="#2A362D"
                    stroke="#D4C5B0"
                    strokeWidth={2}
                    label={{ value: "LOSS-MAKING QUARTER", position: "top", fontSize: 9, fill: "#2A362D", letterSpacing: 2, fontWeight: 700 }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey={metric}
                  stroke={m.color}
                  strokeWidth={2}
                  dot={{ r: 4, fill: m.color, strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: "#D4C5B0", stroke: "#2A362D" }}
                  animationDuration={900}
                  animationEasing="ease-out"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 text-[10px] uppercase tracking-[0.16em] text-fog">
            Illustrative figures from the simulated academic dataset — to be validated against source data
          </p>
        </div>

        <div className="border-t border-hairline lg:col-span-4 lg:border-l lg:border-t-0">
          <div className="grid grid-cols-2 gap-px bg-hairline lg:h-full lg:grid-cols-1">
            {data.quarters.map((q) => (
              <div key={q.q} className="flex items-center justify-between bg-paper p-5" data-testid={`fnb-${hotel}-${q.q.toLowerCase()}`}>
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-fog">{q.q}</span>
                <span className="font-serif text-xl text-ink">
                  {q[metric]} <span className="text-xs text-fog">{m.unit}</span>
                </span>
                {metric === "margin" && q.margin < 0 && (
                  <span className="ml-2 bg-moss px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-champagne">Loss</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-hairline p-8 sm:p-10">
        <p className="max-w-3xl font-serif text-lg italic leading-snug text-smoke">
          In Q3 both hotels posted their highest revenue — and their only losses. Payroll had been committed ahead of demand.
        </p>
      </div>
    </div>
  );
};

/* ---------- PROJECT 04: HILTON PORTFOLIO LENS ---------- */

const HILTON_TABS = [
  { id: "segments", step: "01", name: "Who is it serving?" },
  { id: "tiers", step: "02", name: "How is it structured?" },
  { id: "compete", step: "03", name: "Where does it compete?" },
];

const SEGMENTS = [
  { name: "Business", logic: "Won on consistency, location and loyalty — full-service and focused-service brands carry the weekday base." },
  { name: "Leisure", logic: "Won on experience and destination — lifestyle-leaning brands stretch the portfolio beyond weekdays." },
  { name: "Family", logic: "Won on space, value and predictability — suite and focused-service formats do the heavy lifting." },
  { name: "Group", logic: "Won on scale and event capability — large full-service flags with meetings depth." },
  { name: "Extended Stay", logic: "Won on comfort and cost-per-week — purpose-built formats for longer stays." },
  { name: "Luxury", logic: "Won on service depth and rarity — the halo that lets the portfolio price above itself." },
];

const TIERS = [
  { name: "Luxury", role: "The halo tier — service depth and prestige that lift the whole portfolio.", h: "100%" },
  { name: "Lifestyle", role: "Design-led distinctiveness for experience-driven guests.", h: "82%" },
  { name: "Full Service", role: "The operational core — meetings, events and complete hotel service.", h: "66%" },
  { name: "Focused Service", role: "Efficient, consistent, high-volume — the portfolio's reach engine.", h: "50%" },
  { name: "Extended Stay", role: "Purpose-built for longer stays and cost-per-week economics.", h: "36%" },
];

const COMPETITORS = [
  { name: "Hilton", x: 62, y: 30, note: "Broad portfolio, full-spectrum tier coverage" },
  { name: "Marriott", x: 82, y: 22, note: "Broadest architecture after serial acquisitions" },
  { name: "IHG", x: 45, y: 52, note: "Broad reach, midscale-weighted centre of gravity" },
];

export const HiltonLens = () => {
  const [tab, setTab] = useState("segments");
  const [segment, setSegment] = useState(0);
  const [tier, setTier] = useState(2);

  return (
    <div className="border border-hairline bg-paper" data-testid="hilton-widget">
      <div className="flex flex-nowrap gap-px overflow-x-auto border-b border-hairline bg-hairline" role="tablist" aria-label="Portfolio lenses">
        {HILTON_TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            data-testid={`hilton-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`min-w-[150px] flex-1 whitespace-nowrap px-4 py-4 text-left transition-colors duration-300 ${
              tab === t.id ? "bg-moss text-paper" : "bg-paper text-smoke hover:bg-stone2"
            }`}
          >
            <span className={`block text-[10px] font-semibold tracking-[0.22em] ${tab === t.id ? "text-champagne" : "text-fog"}`}>{t.step}</span>
            <span className="block text-[12px] font-semibold uppercase tracking-[0.12em]">{t.name}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="p-8 sm:p-12"
        >
          {tab === "segments" && (
            <div data-testid="hilton-segments">
              <p className="font-serif text-2xl sm:text-3xl font-light italic text-ink">Select a guest — see the positioning logic.</p>
              <div className="mt-8 grid grid-cols-2 gap-px border border-hairline bg-hairline sm:grid-cols-3 lg:grid-cols-6">
                {SEGMENTS.map((s, i) => (
                  <button
                    key={s.name}
                    onClick={() => setSegment(i)}
                    data-testid={`segment-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    aria-pressed={segment === i}
                    className={`px-4 py-5 text-center text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                      segment === i ? "bg-moss text-paper" : "bg-paper text-smoke hover:bg-stone2"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.p
                  key={segment}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="mt-8 max-w-2xl border-l-2 border-champagne pl-5 font-serif text-xl font-light italic leading-snug text-smoke"
                  data-testid="segment-logic"
                >
                  {SEGMENTS[segment].logic}
                </motion.p>
              </AnimatePresence>
            </div>
          )}

          {tab === "tiers" && (
            <div data-testid="hilton-tiers">
              <p className="font-serif text-2xl sm:text-3xl font-light italic text-ink">A continuum from halo to reach.</p>
              <div className="mt-10 flex h-56 items-end gap-px border border-hairline bg-hairline">
                {TIERS.map((t, i) => (
                  <button
                    key={t.name}
                    onClick={() => setTier(i)}
                    data-testid={`tier-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    aria-pressed={tier === i}
                    className="group relative flex-1 self-stretch"
                  >
                    <motion.div
                      className={`absolute inset-x-0 bottom-0 transition-colors duration-300 ${tier === i ? "bg-moss" : "bg-champagne/60 group-hover:bg-champagne"}`}
                      initial={false}
                      animate={{ height: t.h }}
                      transition={{ duration: 0.6, ease: EASE }}
                    />
                    <span className={`absolute inset-x-0 bottom-2 px-1 text-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${tier === i ? "text-champagne" : "text-ink/70"}`}>
                      {t.name}
                    </span>
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.p
                  key={tier}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="mt-8 max-w-2xl border-l-2 border-champagne pl-5 font-serif text-xl font-light italic leading-snug text-smoke"
                  data-testid="tier-role"
                >
                  {TIERS[tier].role}
                </motion.p>
              </AnimatePresence>
            </div>
          )}

          {tab === "compete" && (
            <div data-testid="hilton-compete">
              <p className="font-serif text-2xl sm:text-3xl font-light italic text-ink">Three global portfolios, one map.</p>
              <div className="mt-8 border border-hairline bg-stone2/60 p-6">
                <svg viewBox="0 0 400 250" className="w-full" role="img" aria-label="Simplified qualitative positioning map of Hilton, Marriott and IHG portfolios">
                  <line x1="40" y1="210" x2="380" y2="210" stroke="#E5E1D8" strokeWidth="1" />
                  <line x1="40" y1="210" x2="40" y2="20" stroke="#E5E1D8" strokeWidth="1" />
                  <text x="210" y="236" textAnchor="middle" fontSize="9.5" fill="#8A8A8A" letterSpacing="2">PORTFOLIO BREADTH →</text>
                  <text x="16" y="120" fontSize="9.5" fill="#8A8A8A" letterSpacing="2" transform="rotate(-90 16 120)">TIER COVERAGE →</text>
                  {COMPETITORS.map((c, i) => (
                    <motion.g
                      key={c.name}
                      initial={{ opacity: 0, scale: 0.6 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + i * 0.15, duration: 0.5, ease: EASE }}
                    >
                      <rect
                        x={40 + c.x * 3.4 - 7}
                        y={20 + c.y * 1.8 - 7}
                        width="14" height="14"
                        fill={c.name === "Hilton" ? "#2A362D" : "#D4C5B0"}
                        transform={`rotate(45 ${40 + c.x * 3.4} ${20 + c.y * 1.8})`}
                      />
                      <text x={40 + c.x * 3.4} y={20 + c.y * 1.8 - 16} textAnchor="middle" fontSize="11" fontWeight="700" fill={c.name === "Hilton" ? "#2A362D" : "#4A4A4A"}>
                        {c.name}
                      </text>
                    </motion.g>
                  ))}
                </svg>
                <ul className="mt-4 flex flex-col gap-2 border-t border-hairline pt-4">
                  {COMPETITORS.map((c) => (
                    <li key={c.name} className="flex items-baseline gap-3 text-sm">
                      <span
                        className={`inline-block h-2.5 w-2.5 shrink-0 translate-y-0.5 rotate-45 ${c.name === "Hilton" ? "bg-moss" : "bg-champagne"}`}
                        aria-hidden="true"
                      />
                      <span className="font-semibold text-ink">{c.name}</span>
                      <span className="text-fog">{c.note}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-fog">
                  Simplified qualitative positioning from the project analysis — no market-share data implied
                </p>
              </div>
              <p className="mt-8 max-w-2xl border-l-2 border-champagne pl-5 font-serif text-xl font-light italic leading-snug text-smoke">
                Breadth is table stakes at this scale. Clarity is the differentiator — each brand needs a distinct guest, promise and role.
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const ProjectWidget = ({ project }) => {
  if (project.slug === "london-conversion-feasibility") return <StagesExplorer stages={project.stages} />;
  if (project.slug === "total-revenue-strategy") return <RevParToggle />;
  if (project.slug === "fnb-performance-analysis") return <FBTimeline />;
  if (project.slug === "hilton-brand-portfolio") return <HiltonLens />;
  return null;
};
